/* Company Confidential, Copyright (c) 2021 Signant Health, Ltd. All Rights Reserved. */

import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Typography } from '@material-ui/core';
import {
  withStyles,
  unstable_createMuiStrictModeTheme as createMuiTheme,
  ThemeProvider,
  StylesProvider,
  createGenerateClassName
} from '@material-ui/core/styles';
import Arrow from '@material-ui/icons/ArrowRight';
import { jwtDecode } from 'jwt-decode';

import config from './config';
import {
  FORMS,
  MAX_FILE_SIZE,
  REQUIRED_FIELDS,
  UCF_DEPENDENT_REQUIRED_FIELDS,
  SCREENS,
  dcfformname,
  ucfformname,
  ocfformname,
  cscfformname
} from './consts';
import { globalStyles } from './styles';
import { postData } from './apiUtil';
import RenderForm from './components/RenderForm';
import SaveScreen from './components/SaveScreen';
import ResponseScreen from './components/ResponseScreen';
import Dialog from './components/Dialog';

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#277EA1',
      light: '#319EC9',
      dark: '#1D5F79'
    }
  },
  typography: {
    h6: {
      fontSize: '1rem',
      fontWeight: 'bold',
      lineHeight: 1.5
    }
  }
});

const generateClassName = createGenerateClassName({
  disableGlobal: true,
  seed: '#edcf'
});

const App = (props) => {
  const { token, sites } = props;
  const tokenPayload = useMemo(() => (token ? (() => { try { return jwtDecode(token); } catch { return {}; } })() : null) || {}, [token]);
  let Forms = [];
  const roles = [];

  if (tokenPayload && tokenPayload.Forms && tokenPayload.Forms.length) {
    const updatedfields = FORMS.filter((item) => tokenPayload.Forms.find((el) => el === item));
    Forms = updatedfields && updatedfields.length ? updatedfields : [];
  }

  if (tokenPayload && tokenPayload.roles) {
    tokenPayload.roles.forEach((item) => roles.push(JSON.parse(item)));
  }

  const computedSites = useMemo(() => {
    if (!sites) return [];
    return sites.length > 1 ? [...sites, 'Not Applicable'] : sites;
  }, [sites]);

  const { formatMessage } = useIntl();
  const defaultView = '';
  const [view, setView] = useState(defaultView);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!tokenPayload) return;
    const v = view.toUpperCase();
    if (v === dcfformname && tokenPayload.dcfFieldsData?.dcfMessage) setMessage(tokenPayload.dcfFieldsData.dcfMessage);
    else if (v === ucfformname && tokenPayload.ucfFieldsData?.ucfMessage) setMessage(tokenPayload.ucfFieldsData.ucfMessage);
    else if (v === ocfformname && tokenPayload.ocfFieldsData?.ocfMessage) setMessage(tokenPayload.ocfFieldsData.ocfMessage);
    else if (v === cscfformname && tokenPayload.cscfFieldsData?.cscfMessage) setMessage(tokenPayload.cscfFieldsData.cscfMessage);
  }, [view, tokenPayload]);

  const [values, setValues] = useState(() => ({
    form: defaultView,
    defaultView,
    priority: formatMessage({ id: 'form.low' }),
    patient: formatMessage({ id: 'form.no' }),
    userChangeType: '',
    userType: '',
    userRole: '',
    additional: '',
    site: '',
    subject: '',
    reason: '',
    change: '',
    confirm: ''
  }));

  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [responseData, setResponseData] = useState({});
  const [dialogProps, setDialogProps] = useState({ visible: false });

  const resetState = () => {
    setValues({
      form: defaultView,
      defaultView,
      priority: formatMessage({ id: 'form.low' }),
      patient: formatMessage({ id: 'form.no' }),
      additional: '',
      site: '',
      subject: '',
      reason: '',
      change: '',
      confirm: ''
    });
    setFiles([]);
    setErrors({});
    setResponseData({});
    setView('');
  };

  const changeForm = (form) => {
    setValues((prev) => ({ ...prev, form, site: computedSites[0] }));
    setView(form);
  };

  const onChange = ({ target: { name, value, checked } }) => {
    setValues((prev) => (name === 'confirm' ? { ...prev, [name]: checked } : { ...prev, [name]: value }));

    const yes = formatMessage({ id: 'form.yes' });
    const no = formatMessage({ id: 'form.no' });
    if (name === 'patient' && value === yes && values[name] === no) {
      setDialogProps({
        visible: true,
        text: formatMessage({ id: 'form.patientAtSiteConfirmation' }),
        okHandler: () => {
          setValues((prev) => ({ ...prev, patient: yes }));
          setDialogProps({ visible: false });
        },
        cancelHandler: () => {
          setValues((prev) => ({ ...prev, patient: no }));
          setDialogProps({ visible: false });
        }
      });
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      if (values.form === 'UCF') {
        Object.keys(UCF_DEPENDENT_REQUIRED_FIELDS).forEach((fieldName) => {
          if (UCF_DEPENDENT_REQUIRED_FIELDS[fieldName] === name && name === 'userChangeType' && value === 'Multi-user') {
            delete next[fieldName];
          }
        });
      }
      return next;
    });
  };

  const getUniqueFiles = (arr) =>
    [...arr].sort((a, b) => (a.path < b.path ? -1 : 1)).filter((item, idx, all) => !idx || item.path !== all[idx - 1].path);

  const onFileChange = (uploadFiles) => {
    if (!uploadFiles?.length) return;
    setFiles((prev) => getUniqueFiles([...prev, ...uploadFiles]));
  };

  const onFileDelete = (index) => {
    setDialogProps({
      visible: true,
      text: formatMessage({ id: 'form.removeAttachment' }),
      yesHandler: () => {
        setFiles((prev) => {
          const next = [...prev];
          next.splice(index, 1);
          return next;
        });
        setDialogProps({ visible: false });
      },
      noHandler: () => setDialogProps({ visible: false })
    });
  };

  const { maxAdditionalContacts } = config;
  const validate = () => {
    const newErrors = { ...errors };
    delete newErrors.confirm;
    REQUIRED_FIELDS.all.forEach((field) => {
      if (!values[field]) newErrors[field] = true;
    });

    if (values.additional !== '') {
      const emails = values.additional.split(/[\s\n,;]+/).filter((str) => str !== '');
      if (emails.length === 0) {
        newErrors.additional = formatMessage({ id: 'error.additional.notRecognized' });
      } else if (maxAdditionalContacts && emails.length > maxAdditionalContacts) {
        newErrors.additional = formatMessage(
          { id: 'error.additional.limitExceeded' },
          { maxAdditionalContacts, enteredAdditionalContacts: emails.length }
        );
      } else {
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        emails.forEach((email) => {
          if (!newErrors.additional && !emailRegex.test(email)) {
            newErrors.additional = formatMessage({ id: 'error.additional.invalidEmail' }, { email });
          }
        });
      }
      if (!newErrors.additional) setValues((prev) => ({ ...prev, additional: emails.join('\n') }));
    }

    if (values.form === 'UCF') {
      if (values.userChangeType === 'Multi-user') setValues((prev) => ({ ...prev, userType: '', userRole: '' }));
      REQUIRED_FIELDS.UCF.forEach((field) => {
        if (field in UCF_DEPENDENT_REQUIRED_FIELDS) {
          const dep = UCF_DEPENDENT_REQUIRED_FIELDS[field];
          if (dep === 'userChangeType' && values[dep] !== 'Multi-user' && !values[field]) newErrors[field] = true;
        } else if (!values[field]) newErrors[field] = true;
      });
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) setView(SCREENS.SAVE);
  };

  const sendData = async () => {
    const data = await postData(token, values, files);
    setResponseData(data);
    setView(SCREENS.RESPONSE);
  };

  const validateConfirm = async () => {
    const newErrors = { ...errors };
    if (!values.confirm) newErrors.confirm = true;
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) await sendData();
  };

  const onHomeButtonClick = () => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next.confirm;
      return next;
    });
    resetState();
  };

  const previous = () => {
    if (FORMS.includes(view)) setView('');
    else if (view === SCREENS.SAVE) setView(values.form);
  };

  const fileValidator = (file) => {
    const sum = files.reduce((s, f) => s + f.size, 0);
    if (sum + file.size > MAX_FILE_SIZE) return { code: 'too-large-file-sum', message: 'Sum of file sizes is too large' };
    return null;
  };

  const onDropRejected = (rejections) => {
    const r = rejections[0];
    if (!r?.errors?.length) return;
    const { file } = r;
    const err = r.errors[0];
    if (err.code === 'file-too-large') {
      setDialogProps({ visible: true, text: `${file.name} ${formatMessage({ id: 'form.filesize' })}`, okHandler: () => setDialogProps({ visible: false }) });
    } else if (err.code === 'too-large-file-sum') {
      setDialogProps({ visible: true, text: formatMessage({ id: 'form.filesizeSum' }, { filename: file.name }), okHandler: () => setDialogProps({ visible: false }) });
    }
  };

  const renderView = (activeView) => {
    const requestorDatetime = new Date();
    switch (activeView) {
      case FORMS[0]:
      case FORMS[1]:
      case FORMS[2]:
      case FORMS[3]:
        return (
          <RenderForm
            formType={activeView}
            onChange={onChange}
            errors={errors}
            values={values}
            setValues={setValues}
            validate={validate}
            files={files}
            fileValidator={fileValidator}
            onFileChange={onFileChange}
            onFileDelete={onFileDelete}
            onDropRejected={onDropRejected}
            resetState={resetState}
            sites={computedSites}
            roles={roles}
            message={message}
          />
        );
      case SCREENS.SAVE:
        return (
          <SaveScreen
            error={errors.confirm}
            onChange={onChange}
            form={activeView}
            values={values}
            files={files}
            validateConfirm={validateConfirm}
            previous={previous}
            message={message}
          />
        );
      case SCREENS.RESPONSE:
        return (
          <ResponseScreen
            formName={values.form}
            onHomeButtonClick={onHomeButtonClick}
            data={responseData}
            requestorDatetime={computedSites.length > 1 ? null : requestorDatetime.toISOString()}
            resetState={resetState}
          />
        );
      default:
        return (
          <div className="page">
            <div className="dataChangeFormHeader">
              {Forms.length ? formatMessage({ id: 'general.dataForms' }) : formatMessage({ id: 'general.noForms' })}
            </div>
            <ul className="form-list">
              {Forms.map((form) => (
                <li key={form}>
                  <button
                    className="button form-button"
                    type="button"
                    onClick={() => changeForm(form)}
                  >
                    <Arrow fontSize="medium" style={{ color: 'orange', fontSize: '22px' }} />{' '}
                    <Typography>
                      <u>
                        <span className="dataFormsList">
                          {`${formatMessage({ id: `response.${form}` })} (${form})`}
                        </span>
                      </u>
                    </Typography>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
    }
  };

  return (
    <StylesProvider generateClassName={generateClassName}>
      <ThemeProvider theme={theme}>
        <div className="page">
          {renderView(view)}
          <Dialog
            visible={dialogProps.visible}
            text={dialogProps.text}
            okHandler={dialogProps.okHandler}
            yesHandler={dialogProps.yesHandler}
            noHandler={dialogProps.noHandler}
            cancelHandler={dialogProps.cancelHandler}
            close={() => setDialogProps({ visible: false })}
          />
        </div>
      </ThemeProvider>
    </StylesProvider>
  );
};

export default withStyles(globalStyles)(App);
